import * as React from 'react';
import './style.scss';
import { AlpacaOrder } from '../../reducers/positionSlice';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend, ChartOptions, Filler, PointElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { dateSort, getDateString } from '../../utils/utils';
import { TableComponent } from '../../components/table';
import { filterOrdersByPeriod, TradeHistoryEntry } from '../../utils/orderPnL';

interface OrdersPageProps {
    orders: AlpacaOrder[];
    tradeHistory: Record<string, TradeHistoryEntry[]>;
    chartPeriod: string;
    periodStartDate: string;
    profitLoss: number;
    updateOrders: (period: string) => void;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend, Filler, PointElement);

export const OrdersPage: React.FC<OrdersPageProps> = ({ orders, tradeHistory, chartPeriod, periodStartDate, profitLoss, updateOrders }) => {
    const [activeTab, setActiveTab] = React.useState<'all' | 'buy' | 'sell'>('all');
    const ordersInPeriod = React.useMemo(
        () => filterOrdersByPeriod(orders, periodStartDate),
        [orders, periodStartDate]
    );

    // Process sell orders for chart
    let sellOrders: Array<{ date: string; label: string; profitLoss: number; symbol: string }> = [];
    Object.entries(tradeHistory).forEach(([key, histories]) => {
        histories.forEach((history) => {
            if (history.side === 'sell' && history.profitLoss !== undefined) {
                sellOrders.push({
                    date: history.date,
                    label: `${key} - ${getDateString(new Date(history.date))}`,
                    symbol: key,
                    profitLoss: history.profitLoss,
                });
            }
        });
    });
    sellOrders = sellOrders.sort((a, b) => dateSort('asc', a.date, b.date));

    // Calculate statistics
    const totalOrders = ordersInPeriod.length;
    const buyOrders = ordersInPeriod.filter(o => o.side === 'buy').length;
    const sellOrders_count = ordersInPeriod.filter(o => o.side === 'sell').length;
    const totalProfitableSells = sellOrders.filter(pl => pl.profitLoss > 0).length;
    const totalLosingTrades = sellOrders.filter(pl => pl.profitLoss < 0).length;
    const winRate = sellOrders.length > 0 ? ((totalProfitableSells / sellOrders.length) * 100).toFixed(1) : '0';

    // Chart data
    const chartLabels = sellOrders.map(o => o.label);
    const chartData = sellOrders.map(o => o.profitLoss);
    const backgroundColors = chartData.map(val => val >= 0 ? 'rgba(0, 200, 20, 0.7)' : 'rgba(255, 80, 0, 0.7)');
    const borderColors = chartData.map(val => val >= 0 ? 'rgb(0, 200, 20)' : 'rgb(255, 80, 0)');
    
    // Calculate cumulative P/L
    let cumulativeSum = 0;
    const cumulativePL = chartData.map(val => {
        cumulativeSum += val;
        return cumulativeSum;
    });
    
    // Calculate additional metrics
    const totalTradePL = chartData.reduce((sum, val) => sum + val, 0);
    const largestWin = Math.max(...chartData);
    const largestLoss = Math.min(...chartData);
    const avgPL = chartData.length > 0 ? totalTradePL / chartData.length : 0;
    const profitFactor = totalProfitableSells > 0 && totalLosingTrades > 0 
        ? (sellOrders.filter(pl => pl.profitLoss > 0).reduce((sum, o) => sum + o.profitLoss, 0) / 
           Math.abs(sellOrders.filter(pl => pl.profitLoss < 0).reduce((sum, o) => sum + o.profitLoss, 0)))
        : totalProfitableSells > 0 ? 999 : 0;

    const data = {
        labels: chartLabels,
        datasets: [
            {
                type: 'bar' as const,
                label: 'Trade P/L',
                data: chartData,
                backgroundColor: backgroundColors,
                borderColor: borderColors,
                borderWidth: 1.5,
                borderRadius: 4,
                order: 2,
                yAxisID: 'y',
            },
            {
                type: 'line' as const,
                label: 'Cumulative P/L',
                data: cumulativePL,
                borderColor: totalTradePL >= 0 ? 'rgb(0, 180, 20)' : 'rgb(255, 100, 0)',
                backgroundColor: totalTradePL >= 0 ? 'rgba(0, 180, 20, 0.05)' : 'rgba(255, 100, 0, 0.05)',
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: totalTradePL >= 0 ? 'rgb(0, 180, 20)' : 'rgb(255, 100, 0)',
                pointBorderColor: 'white',
                pointBorderWidth: 2,
                order: 1,
                yAxisID: 'y1',
            }
        ],
    };

    const options: ChartOptions<'bar' | 'line'> = {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: { size: 13, weight: 'bold' },
                    color: 'var(--text-primary)',
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                padding: 14,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 6,
                displayColors: true,
                callbacks: {
                    title: function (context) {
                        return context[0].label?.split(' - ')[0] || '';
                    },
                    label: function (context) {
                        const value = context.parsed.y;
                        if (context.dataset.label === 'Cumulative P/L') {
                            return `Cumulative: $${value.toFixed(2)}`;
                        }
                        const color = value >= 0 ? '✓ PROFIT' : '✗ LOSS';
                        return `Trade P/L: $${value.toFixed(2)} (${color})`;
                    }
                }
            },
        },
        scales: {
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return '$' + value;
                    },
                    color: 'var(--text-secondary)',
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.08)',
                    drawTicks: true,
                },
                title: {
                    display: true,
                    text: 'Trade P/L ($)',
                    color: 'var(--text-secondary)',
                }
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return '$' + value;
                    },
                    color: totalTradePL >= 0 ? 'rgb(0, 180, 20)' : 'rgb(255, 100, 0)',
                },
                grid: {
                    drawOnChartArea: false,
                    drawTicks: false,
                },
                title: {
                    display: true,
                    text: 'Cumulative P/L ($)',
                    color: totalTradePL >= 0 ? 'rgb(0, 180, 20)' : 'rgb(255, 100, 0)',
                }
            },
            x: {
                grid: {
                    display: false,
                    drawTicks: false,
                },
                ticks: {
                    color: 'var(--text-secondary)',
                }
            }
        }
    };

    // Prepare orders for table
    const filteredOrders = activeTab === 'all'
        ? ordersInPeriod
        : ordersInPeriod.filter(o => o.side === activeTab);

    const orderColumns = [
        {
            key: 'symbol',
            label: 'Symbol',
            renderer: (row: AlpacaOrder) => <span style={{ fontWeight: 600 }}>{row.symbol}</span>
        },
        {
            key: 'side',
            label: 'Type',
            renderer: (row: AlpacaOrder) => (
                <span className={`order-type ${row.side}`} style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: row.side === 'buy' ? 'rgba(0, 200, 20, 0.1)' : 'rgba(255, 80, 0, 0.1)',
                    color: row.side === 'buy' ? 'rgb(0, 200, 20)' : 'rgb(255, 80, 0)',
                }}>
                    {row.side.toUpperCase()}
                </span>
            )
        },
        {
            key: 'qty',
            label: 'Qty',
            renderer: (row: AlpacaOrder) => parseFloat(row.qty).toFixed(2)
        },
        {
            key: 'price',
            label: 'Price',
            renderer: (row: AlpacaOrder) => `$${parseFloat(row.price).toFixed(2)}`
        },
        {
            key: 'order_status',
            label: 'Status',
            renderer: (row: AlpacaOrder) => (
                <span style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: row.order_status === 'filled' ? 'rgba(100, 150, 255, 0.1)' : 'rgba(200, 150, 100, 0.1)',
                    color: row.order_status === 'filled' ? 'rgb(100, 150, 255)' : 'rgb(200, 150, 100)',
                }}>
                    {row.order_status.charAt(0).toUpperCase() + row.order_status.slice(1)}
                </span>
            )
        },
        {
            key: 'transaction_time',
            label: 'Date/Time',
            renderer: (row: AlpacaOrder) => new Date(row.transaction_time).toLocaleString()
        }
    ];

    return (
        <div className="orders-page">
            {/* Header Section */}
            <div className='page-header'>
                <h1>Orders & Trade History</h1>
                <p>Track all your trades and analyze performance</p>
            </div>

            {/* Statistics Cards */}
            <div className='stats-grid'>
                <div className='stat-card'>
                    <div className='stat-label'>Total Orders</div>
                    <div className='stat-value'>{totalOrders}</div>
                    <div className='stat-detail'>{buyOrders} buy / {sellOrders_count} sell</div>
                </div>
                <div className='stat-card'>
                    <div className='stat-label'>Total P/L</div>
                    <div className={`stat-value ${profitLoss >= 0 ? 'positive' : 'negative'}`}>
                        ${profitLoss.toFixed(2)}
                    </div>
                    <div className='stat-detail'>{sellOrders.length} closed trades</div>
                </div>
                <div className='stat-card'>
                    <div className='stat-label'>Win Rate</div>
                    <div className='stat-value success'>{winRate}%</div>
                    <div className='stat-detail'>{totalProfitableSells} wins / {totalLosingTrades} losses</div>
                </div>
                <div className='stat-card'>
                    <div className='stat-label'>Avg P/L</div>
                    <div className={`stat-value ${sellOrders.length > 0 && (sellOrders.reduce((sum, o) => sum + o.profitLoss, 0) / sellOrders.length) >= 0 ? 'positive' : 'negative'}`}>
                        ${sellOrders.length > 0 ? (sellOrders.reduce((sum, o) => sum + o.profitLoss, 0) / sellOrders.length).toFixed(2) : '0.00'}
                    </div>
                    <div className='stat-detail'>Per closed trade</div>
                </div>
            </div>

            {/* Profit/Loss Chart Section */}
            <div className='chart-section'>
                <div className='section-header'>
                    <h2>Profit/Loss by Trade</h2>
                    <div className='period-selector'>
                        <button 
                            className={chartPeriod === '1D' ? 'active' : ''} 
                            onClick={() => updateOrders('1D')}
                        >1D</button>
                        <button 
                            className={chartPeriod === '1W' ? 'active' : ''} 
                            onClick={() => updateOrders('1W')}
                        >1W</button>
                        <button 
                            className={chartPeriod === '1M' ? 'active' : ''} 
                            onClick={() => updateOrders('1M')}
                        >1M</button>
                        <button 
                            className={chartPeriod === '3M' ? 'active' : ''} 
                            onClick={() => updateOrders('3M')}
                        >3M</button>
                        <button 
                            className={chartPeriod === 'YTD' ? 'active' : ''} 
                            onClick={() => updateOrders('YTD')}
                        >YTD</button>
                        <button 
                            className={chartPeriod === '1Y' ? 'active' : ''} 
                            onClick={() => updateOrders('1Y')}
                        >1Y</button>
                    </div>
                </div>
                <div className='chart-container'>
                    {sellOrders.length > 0 ? (
                        <Bar data={data as any} options={options} />
                    ) : (
                        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <p>No trade data available for the selected period</p>
                            <p style={{ fontSize: '14px', marginTop: '8px' }}>Try selecting a different time period</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Orders Table Section */}
            <div className='table-section'>
                <div className='section-header'>
                    <h2>Order Details</h2>
                    <div className='tab-selector'>
                        <button 
                            className={activeTab === 'all' ? 'active' : ''} 
                            onClick={() => setActiveTab('all')}
                        >
                            All ({totalOrders})
                        </button>
                        <button 
                            className={activeTab === 'buy' ? 'active' : ''} 
                            onClick={() => setActiveTab('buy')}
                        >
                            Buy ({buyOrders})
                        </button>
                        <button 
                            className={activeTab === 'sell' ? 'active' : ''} 
                            onClick={() => setActiveTab('sell')}
                        >
                            Sell ({sellOrders_count})
                        </button>
                    </div>
                </div>
                <TableComponent data={filteredOrders} columns={orderColumns} itemsPerPage={50} />
            </div>
        </div>
    );
};